import React, { useState } from 'react';
import '../RodCuttingAlgo.css';
import {
  Layout,
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Table,
  Typography,
  Space,
  Divider,
  Row,
  Col,
  Statistic,
  Alert,
  notification,
  Tag,
  Tooltip,
  Modal,
  Progress,
  Badge,
  Radio,
  Switch,
  Tabs
} from 'antd';
import {
  PlayCircleOutlined,
  ReloadOutlined,
  FileTextOutlined,
  ScissorOutlined,
  BarChartOutlined,
  DollarOutlined,
  ThunderboltOutlined,
  ClearOutlined,
  PlusOutlined,
  DeleteOutlined,
  CalculatorOutlined,
  RocketOutlined,
  DoubleRightOutlined
} from '@ant-design/icons';

const { Content } = Layout;
const { Title, Text } = Typography;

const RodCuttingAlgo = () => {
  const [form] = Form.useForm();
  const [rodLength, setRodLength] = useState(1000);
  const [cuttingData, setCuttingData] = useState([
    { key: 1, length: 150, quantity: 50 },
    { key: 2, length: 120, quantity: 35 },
    { key: 3, length: 100, quantity: 25 },
    { key: 4, length: 80, quantity: 60 },
    { key: 5, length: 60, quantity: 70 }
  ]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [keyCounter, setKeyCounter] = useState(6);
  const [selectedAlgorithm, setSelectedAlgorithm] = useState('basic'); // 'basic' 또는 'pattern'
  const [comparisonMode, setComparisonMode] = useState(false); // 비교 모드
  const [comparisonResults, setComparisonResults] = useState({ basic: [], pattern: [] }); // 비교 결과

  // 행 추가
  const addRow = () => {
    setCuttingData([...cuttingData, { 
      key: keyCounter, 
      length: null, 
      quantity: null 
    }]);
    setKeyCounter(keyCounter + 1);
  };

  // 행 삭제
  const removeRow = (key) => {
    if (cuttingData.length > 1) {
      setCuttingData(cuttingData.filter(item => item.key !== key));
    }
  };

  // 데이터 업데이트
  const updateRow = (key, field, value) => {
    setCuttingData(cuttingData.map(item => 
      item.key === key ? { ...item, [field]: value } : item
    ));
  };

  // 예시 데이터 로드
  const loadExample = () => {
    setRodLength(1000);
    setCuttingData([
      { key: 1, length: 150, quantity: 50 },
      { key: 2, length: 120, quantity: 35 },
      { key: 3, length: 100, quantity: 25 },
      { key: 4, length: 80, quantity: 60 },
      { key: 5, length: 60, quantity: 70 }
    ]);
    setKeyCounter(6);
    setResults([]);
    form.setFieldsValue({ rodLength: 1000 });
    notification.success({
      message: '예시 데이터 로드 완료',
      description: '1000mm 막대기에 5가지 절단 요구사항이 설정되었습니다.',
      duration: 3
    });
  };

  // 초기화
  const resetData = () => {
    Modal.confirm({
      title: '데이터 초기화',
      content: '모든 입력 데이터와 결과를 초기화하시겠습니까?',
      onOk() {
        setRodLength(1000);
        setCuttingData([{ key: 1, length: null, quantity: null }]);
        setKeyCounter(2);
        setResults([]);
        form.resetFields();
        notification.info({
          message: '데이터 초기화 완료',
          description: '모든 입력값이 초기화되었습니다.',
          duration: 3
        });
      }
    });
  };

  // 실행
  const executeOptimization = async () => {
    try {
      const validData = cuttingData.filter(item => item.length > 0 && item.quantity > 0);
      
      if (rodLength <= 0) {
        notification.error({
          message: '입력 오류',
          description: '금속막대길이를 올바르게 입력해주세요. (1mm 이상)',
          duration: 4
        });
        return;
      }

      if (validData.length === 0) {
        notification.error({
          message: '입력 오류', 
          description: '절단 길이와 수량을 하나 이상 입력해주세요.',
          duration: 4
        });
        return;
      }

      const invalidItems = validData.filter(item => item.length >= rodLength);
      if (invalidItems.length > 0) {
        notification.error({
          message: '입력 오류',
          description: '절단 길이는 막대 길이보다 작아야 합니다.',
          duration: 4
        });
        return;
      }

      setLoading(true);
      setResults([]);

      // 🚀 알고리즘 실행 (선택된 방식에 따라)
      let calculatedResults;
      let algorithmName;
      
      if (comparisonMode) {
        // 비교 모드: 두 알고리즘 모두 실행
        const basicOptimizer = new RodCuttingOptimizer(rodLength, validData);
        const patternOptimizer = new PatternBasedOptimizer(rodLength, validData);
        
        const basicResults = basicOptimizer.optimize();
        const patternResults = patternOptimizer.optimize();
        
        setComparisonResults({ basic: basicResults, pattern: patternResults });
        
        // 🎯 더 효율적인 알고리즘 결과를 기준으로 표시
        const basicRods = basicResults.reduce((sum, r) => sum + r.quantity, 0);
        const patternRods = patternResults.reduce((sum, r) => sum + r.quantity, 0);
        
        if (basicRods <= patternRods) {
          calculatedResults = basicResults;
          algorithmName = "비교 모드 (Branch and Bound - 더 효율적)";
        } else {
          calculatedResults = patternResults;
          algorithmName = "비교 모드 (Column Generation - 더 효율적)";
        }
        
      } else {
        // 단일 모드: 선택된 알고리즘만 실행
        if (selectedAlgorithm === 'pattern') {
          const optimizer = new PatternBasedOptimizer(rodLength, validData);
          calculatedResults = optimizer.optimize();
          algorithmName = "Column Generation Algorithm";
        } else {
          const optimizer = new RodCuttingOptimizer(rodLength, validData);
          calculatedResults = optimizer.optimize();
          algorithmName = "Branch and Bound Algorithm";
        }
      }
      
      if (calculatedResults.length === 0) {
        notification.error({
          message: '최적화 실패',
          description: '조건에 맞는 절단 계획을 찾을 수 없습니다.',
          duration: 4
        });
        return;
      }

      // 결과 포맷팅
      const formattedResults = calculatedResults.map((result, index) => ({
        key: index + 1,
        case: `Case-${index + 1}`,
        quantity: result.quantity,
        ...result.cuts.reduce((acc, cut, cutIndex) => {
          acc[`cut_${cutIndex}`] = cut;
          return acc;
        }, {}),
        totalLength: result.totalLength,
        loss: result.loss,
        efficiency: ((1 - result.loss / rodLength) * 100).toFixed(1)
      }));

      setResults(formattedResults);

      const totalRods = calculatedResults.reduce((sum, r) => sum + r.quantity, 0);
      const totalWaste = calculatedResults.reduce((sum, r) => sum + (r.loss * r.quantity), 0);
      const efficiency = ((1 - totalWaste/(totalRods * rodLength)) * 100).toFixed(1);

      notification.success({
        message: `${algorithmName} 최적화 완료!`,
        description: `총 ${totalRods}개 막대 필요, 재료 효율성 ${efficiency}%`,
        duration: 5
      });

    } catch (error) {
      notification.error({
        message: '계산 오류',
        description: `계산 중 오류가 발생했습니다: ${error.message}`,
        duration: 4
      });
    } finally {
      setLoading(false);
    }
  };

  // 절단 테이블 컬럼 정의
  const cuttingColumns = [
    {
      title: '번호',
      dataIndex: 'key',
      key: 'key',
      width: 60,
      align: 'center',
      render: (text, record, index) => index + 1
    },
    {
      title: '길이 (mm)',
      dataIndex: 'length',
      key: 'length',
      width: 150,
             render: (value, record) => (
         <InputNumber
           value={value}
           min={1}
           max={rodLength - 1}
           placeholder="예: 150"
           onChange={(val) => updateRow(record.key, 'length', val)}
           style={{ width: '100%', height: '28px' }}
           size="small"
         />
       )
    },
    {
      title: '수량 (개)',
      dataIndex: 'quantity', 
      key: 'quantity',
      width: 150,
             render: (value, record) => (
         <InputNumber
           value={value}
           min={1}
           max={10000}
           placeholder="예: 100"
           onChange={(val) => updateRow(record.key, 'quantity', val)}
           style={{ width: '100%', height: '28px' }}
           size="small"
         />
       )
    },
    {
      title: '작업',
      key: 'action',
      width: 80,
      align: 'center',
      render: (_, record) => (
                 <Tooltip title="행 삭제">
           <Button
             type="text"
             danger
             icon={<DeleteOutlined />}
             disabled={cuttingData.length === 1}
             onClick={() => removeRow(record.key)}
             size="small"
             style={{ height: '28px' }}
           />
         </Tooltip>
      )
    }
  ];

  // 결과 테이블 컬럼 생성
  const resultColumns = [
    {
      title: 'Case',
      dataIndex: 'case',
      key: 'case',
      width: 100,
      fixed: 'left',
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: '수량',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'center',
      render: (text) => <Text strong>{text}개</Text>
    }
  ];

  // 동적으로 절단 길이 컬럼 추가
  const validCuttingData = cuttingData.filter(item => item.length > 0);
  validCuttingData.forEach((item, index) => {
    resultColumns.push({
      title: `${item.length}mm`,
      dataIndex: `cut_${index}`,
      key: `cut_${index}`,
      width: 80,
      align: 'center',
      render: (text) => text || 0
    });
  });

  resultColumns.push(
    {
      title: '총 길이',
      dataIndex: 'totalLength',
      key: 'totalLength',
      width: 100,
      align: 'center',
      render: (text) => <Text>{text}mm</Text>
    },
    {
      title: 'Loss',
      dataIndex: 'loss',
      key: 'loss',
      width: 100,
      align: 'center',
      render: (text) => <Tag color={text === 0 ? 'green' : 'red'} style={{fontWeight: 'bold'}}>{text}mm</Tag>
    },
    {
      title: '효율성',
      dataIndex: 'efficiency',
      key: 'efficiency',
      width: 100,
      align: 'center',
      render: (text) => <Text type="success">{text}%</Text>
    }
  );

  // 통계 계산
  const totalRods = results.reduce((sum, r) => sum + r.quantity, 0);
  const totalWaste = results.reduce((sum, r) => sum + (r.loss * r.quantity), 0);
  const totalEfficiency = totalRods > 0 ? 
    ((1 - totalWaste/(totalRods * rodLength)) * 100).toFixed(1) : 0;
  const costSaving = totalEfficiency;

  // 각 길이별 총 절단 개수 계산
  const calculateTotalCuts = () => {
    const validCuttingData = cuttingData.filter(item => item.length > 0);
    return validCuttingData.map((cuttingItem, index) => {
      const totalCut = results.reduce((sum, result) => {
        return sum + (result[`cut_${index}`] || 0) * result.quantity;
      }, 0);
      const required = cuttingItem.quantity || 0;
      const progress = required > 0 ? (totalCut / required) * 100 : 0;
      
      return {
        length: cuttingItem.length,
        totalCut,
        required,
        progress: progress.toFixed(1),
        status: totalCut >= required ? 'success' : totalCut > 0 ? 'active' : 'exception',
        isOverProduced: totalCut > required
      };
    });
  };

  const totalCutsData = results.length > 0 ? calculateTotalCuts() : [];

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      <Content style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
       
        {/* 입력 섹션 */}
        <Card title="절단 요구사항 입력" style={{ marginBottom: '16px' }}>
          <Form form={form} layout="vertical">
            {/* 알고리즘 선택 */}
            <Row gutter={24} >
              <Col xs={24} sm={12} md={16}>
                <Form.Item label="알고리즘 선택">
                  <Radio.Group 
                    value={selectedAlgorithm} 
                    onChange={(e) => setSelectedAlgorithm(e.target.value)}
                    buttonStyle="solid"
                    style={{ width: '100%', display: 'flex' }}
                  >
                    <Radio.Button value="basic" style={{ flex: 1, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                      <Space>
                        <CalculatorOutlined />
                        Branch & Bound
                      </Space>
                    </Radio.Button>
                    <Radio.Button value="pattern" style={{ flex: 1, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                      <Space>
                        <RocketOutlined />
                        Column Generation
                      </Space>
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item label="비교 모드">
                  <Switch
                    checked={comparisonMode}
                    onChange={setComparisonMode}
                    checkedChildren="ON"
                    unCheckedChildren="OFF"
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    두 알고리즘 성능 비교
                  </div>
                </Form.Item>
              </Col>
            </Row>
            
                         {/* 알고리즘 설명 */}
             <Row gutter={24} style={{ marginBottom: '16px', marginTop: '-20px' }}>
              <Col span={24}>
                {selectedAlgorithm === 'basic' ? (
                  <Alert
                    message="Branch and Bound Algorithm"
                    description={
                      <div>
                        <div><strong>🎯 완전 탐색 + 가지치기 방식을 정확히 표현</strong></div>
                        <div>• 최적화 분야에서 널리 알려진 용어</div>
                        <div>• 10mm씩 감소하는 방식을 잘 설명</div>
                        <div>• 모든 가능한 조합을 체계적으로 탐색하여 최적해 보장</div>
                      </div>
                    }
                    type="info"
                    showIcon
                    style={{ backgroundColor: '#f0f2ff', border: '1px solid #d6e4ff' }}
                  />
                ) : (
                  <Alert
                    message="Column Generation Algorithm"
                    description={
                      <div>
                        <div><strong>🚀 Rod Cutting 분야에서 가장 유명한 알고리즘</strong></div>
                        <div>• 학술 논문에서 자주 사용</div>
                        <div>• 0폐기 패턴 우선 적용 방식과 정확히 일치</div>
                        <div>• 패턴 생성 기반으로 폐기물 최소화에 특화</div>
                      </div>
                    }
                    type="success"
                    showIcon
                    style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}
                  />
                )}
              </Col>
            </Row>
            
            {/* 막대 길이 입력 */}
            <Row gutter={24}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  label="금속막대길이"
                  name="rodLength"
                  initialValue={rodLength}
                  rules={[
                    { required: true, message: '금속막대길이를 입력해주세요!' },
                    { type: 'number', min: 1, message: '1mm 이상 입력해주세요!' }
                  ]}
                >
                                     <InputNumber
                     value={rodLength}
                     min={1}
                     max={100000}
                     addonAfter="mm"
                     placeholder="1000"
                     style={{ width: '100%' }}
                     size="small"
                     onChange={setRodLength}
                   />
                </Form.Item>
              </Col>
            </Row>

                         {/* 절단 요구사항 테이블 */}
             <Form.Item label="절단 요구사항" style={{marginTop: '-15px' }}>
               <Table
                 columns={cuttingColumns}
                 dataSource={cuttingData}
                 pagination={false}
                 size="small"
                 bordered
                 scroll={{ x: 500 }}
                 className="compact-table"
                 footer={() => (
                   <Button
                     type="dashed"
                     onClick={addRow}
                     icon={<PlusOutlined />}
                     style={{ width: '100%', height: '32px' }}
                   >
                     절단 항목 추가
                   </Button>
                 )}
               />
            </Form.Item>

            {/* 버튼 그룹 */}
                         <Row gutter={16} justify="center">
               <Col>
                 <Button
                   type="default"
                   icon={<FileTextOutlined />}
                   onClick={loadExample}
                   style={{ height: '32px' }}
                 >
                   예시 데이터
                 </Button>
               </Col>
               <Col>
                 <Button
                   type="default"
                   icon={<ReloadOutlined />}
                   onClick={resetData}
                   style={{ height: '32px' }}
                 >
                   초기화
                 </Button>
               </Col>
               <Col>
                 <Button
                   type="primary"
                   icon={<CalculatorOutlined />}
                   loading={loading}
                   onClick={executeOptimization}
                   style={{ height: '32px' }}
                 >
                   {loading ? '계산 중...' : '최적화 실행'}
                 </Button>
               </Col>
             </Row>
          </Form>
        </Card>

        {/* 결과 섹션 */}
        {results.length > 0 && (
          <>
            {/* 통계 카드 */}
                         <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col xs={12} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="총 필요 막대 수"
                    value={totalRods}
                    suffix="개"
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="총 폐기물"
                    value={totalWaste}
                    suffix="mm"
                    valueStyle={{ color: '#fa8c16' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="재료 효율성"
                    value={totalEfficiency}
                    suffix="%"
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="비용 절약률"
                    value={costSaving}
                    suffix="%"
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* 결과 테이블 */}
            <Card title={
              <Space>
                <BarChartOutlined />
                <span>최적화 결과</span>
              </Space>
            }>
                             <Table
                 columns={resultColumns}
                 dataSource={results}
                 scroll={{ x: 800 }}
                 pagination={false}
                 bordered
                 size="small"
                 className="compact-table"
                summary={() => {
                  const validCuttingData = cuttingData.filter(item => item.length > 0);
                  
                  return (
                    <Table.Summary.Row style={{ backgroundColor: '#f0f2f5', fontWeight: 'bold', textAlign: 'center' }}>
                      <Table.Summary.Cell index={0} style={{ textAlign: 'center' }}>
                        <Tag color="purple">총 합계</Tag>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} style={{ textAlign: 'center' }}>
                        <Text strong style={{ color: '#1890ff' }}>{totalRods}개</Text>
                      </Table.Summary.Cell>
                      {validCuttingData.map((_, index) => {
                        const totalCut = results.reduce((sum, result) => {
                          return sum + (result[`cut_${index}`] || 0) * result.quantity;
                        }, 0);
                        return (
                          <Table.Summary.Cell key={index} index={index + 2} style={{ textAlign: 'center' }}>
                            <Text strong style={{ color: '#52c41a' }}>{totalCut}</Text>
                          </Table.Summary.Cell>
                        );
                      })}
                      <Table.Summary.Cell index={validCuttingData.length + 2} style={{ textAlign: 'center' }}>
                        <Text strong>총 사용량</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={validCuttingData.length + 3} style={{ textAlign: 'center' }}>
                        <Text strong style={{ color: '#fa8c16' }}>{totalWaste}mm</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={validCuttingData.length + 4} style={{ textAlign: 'center' }}>
                        <Text strong style={{ color: '#722ed1' }}>{totalEfficiency}%</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
              
              {/* 총합계 상세 정보 */}
              <Divider orientation="left">절단 요구사항 달성 현황</Divider>
              
                             <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                {totalCutsData.map((item, index) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={index}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <div style={{ marginBottom: '8px' }}>
                        <Badge 
                          count={`${item.length}mm`} 
                          style={{ 
                            backgroundColor: item.status === 'success' ? '#52c41a' : 
                                           item.status === 'active' ? '#1890ff' : '#ff4d4f'
                          }} 
                        />
                      </div>
                      <Progress
                        type="circle"
                        size={80}
                        percent={Math.min(parseFloat(item.progress), 100)}
                        status={item.status}
                        format={() => `${item.totalCut}/${item.required}`}
                        strokeColor={item.isOverProduced ? '#fa8c16' : undefined}
                      />
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                        {item.totalCut >= item.required ? 
                          <div>
                            <Text type="success">✅ 요구량 달성</Text>
                            {item.totalCut > item.required && (
                              <div style={{ marginTop: '2px' }}>
                                <Text type="warning" style={{ fontSize: '11px' }}>
                                  +{item.totalCut - item.required}개 과다생산
                                </Text>
                              </div>
                            )}
                          </div> : 
                          <Text type="warning">⚠️ 부족 {item.required - item.totalCut}개</Text>
                        }
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>

              {/* 전체 요약 */}
              <Card size="small" style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
                <Row gutter={16} align="middle">
                  <Col flex="auto">
                    <Space size="large">
                      <Statistic 
                        title="생산 계획" 
                        value={results.length} 
                        suffix="개" 
                        valueStyle={{ color: '#1890ff', fontSize: '18px' }}
                      />
                      <Statistic 
                        title="총 막대 소요" 
                        value={totalRods} 
                        suffix="개" 
                        valueStyle={{ color: '#722ed1', fontSize: '18px' }}
                      />
                      <Statistic 
                        title="재료 효율성" 
                        value={totalEfficiency} 
                        suffix="%" 
                        valueStyle={{ color: '#52c41a', fontSize: '18px' }}
                      />
                      <Statistic 
                        title="총 폐기물" 
                        value={totalWaste} 
                        suffix="mm" 
                        valueStyle={{ color: '#fa8c16', fontSize: '18px' }}
                      />
                    </Space>
                  </Col>
                  <Col>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                        전체 효율성
                      </div>
                      <Progress
                        type="dashboard"
                        percent={parseFloat(totalEfficiency)}
                        size={100}
                        status={parseFloat(totalEfficiency) >= 90 ? 'success' : 
                               parseFloat(totalEfficiency) >= 70 ? 'active' : 'exception'}
                      />
                    </div>
                  </Col>
                </Row>
              </Card>
              
              <Alert
                message="최적화 완료"
                description={
                  <div>
                    <div style={{ marginBottom: '8px' }}>
                      총 {results.length}개의 최적 절단 계획이 생성되었습니다. 재료 효율성 {totalEfficiency}%를 달성했으며, {totalWaste}mm의 폐기물이 발생합니다.
                    </div>
                    <div style={{ fontSize: '13px', color: '#666', borderTop: '1px solid #f0f0f0', paddingTop: '8px' }}>
                      <strong>💡 참고:</strong> Rod Cutting 알고리즘은 막대 활용도를 최대화하기 위해 여러 길이를 조합합니다. 
                      이 과정에서 특정 길이가 요구량보다 많이 생산될 수 있으며, 이는 <strong>정상적인 현상</strong>입니다.
                      과다생산된 부분은 다음 생산에 활용하거나 재고로 관리하시면 됩니다.
                    </div>
                  </div>
                }
                type="success"
                showIcon
                style={{ marginTop: '12px' }}
              />
            </Card>
          </>
        )}
        
        {/* 🔄 비교 모드 결과 */}
        {comparisonMode && comparisonResults.basic.length > 0 && comparisonResults.pattern.length > 0 && (
          <Card 
            title={
              <Space>
                <DoubleRightOutlined />
                <span>알고리즘 성능 비교</span>
              </Space>
            } 
                         style={{ marginTop: '12px' }}
          >
            {/* 성능 비교 요약 */}
            <Row gutter={24} style={{ marginBottom: '16px' }}>
              <Col xs={24} md={12}>
                <Card size="small" title="🔵 Branch and Bound Algorithm" headStyle={{ backgroundColor: '#f0f2ff' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Statistic 
                      title="필요 막대 수" 
                      value={comparisonResults.basic.reduce((sum, r) => sum + r.quantity, 0)} 
                      suffix="개"
                      valueStyle={{ color: '#1890ff' }}
                    />
                    <Statistic 
                      title="총 폐기물" 
                      value={comparisonResults.basic.reduce((sum, r) => sum + (r.loss * r.quantity), 0)} 
                      suffix="mm"
                      valueStyle={{ color: '#fa8c16' }}
                    />
                    <Statistic 
                      title="재료 효율성" 
                      value={((1 - comparisonResults.basic.reduce((sum, r) => sum + (r.loss * r.quantity), 0) / (comparisonResults.basic.reduce((sum, r) => sum + r.quantity, 0) * rodLength)) * 100).toFixed(1)} 
                      suffix="%"
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Space>
                </Card>
              </Col>
                              <Col xs={24} md={12}>
                  <Card size="small" title="🚀 Column Generation Algorithm" headStyle={{ backgroundColor: '#f6ffed' }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Statistic 
                        title="필요 막대 수" 
                        value={comparisonResults.pattern.reduce((sum, r) => sum + r.quantity, 0)} 
                        suffix="개"
                        valueStyle={{ color: '#1890ff' }}
                      />
                      <Statistic 
                        title="총 폐기물" 
                        value={comparisonResults.pattern.reduce((sum, r) => sum + (r.loss * r.quantity), 0)} 
                        suffix="mm"
                        valueStyle={{ color: '#fa8c16' }}
                      />
                      <Statistic 
                        title="재료 효율성" 
                        value={((1 - comparisonResults.pattern.reduce((sum, r) => sum + (r.loss * r.quantity), 0) / (comparisonResults.pattern.reduce((sum, r) => sum + r.quantity, 0) * rodLength)) * 100).toFixed(1)} 
                        suffix="%"
                        valueStyle={{ color: '#52c41a' }}
                      />
                    </Space>
                  </Card>
                </Col>
            </Row>

            {/* 개선 효과 */}
            <Row gutter={24} style={{ marginBottom: '16px' }}>
              <Col span={24}>
                <Alert
                  message="개선 효과"
                  description={(() => {
                    const basicRods = comparisonResults.basic.reduce((sum, r) => sum + r.quantity, 0);
                    const patternRods = comparisonResults.pattern.reduce((sum, r) => sum + r.quantity, 0);
                    const basicWaste = comparisonResults.basic.reduce((sum, r) => sum + (r.loss * r.quantity), 0);
                    const patternWaste = comparisonResults.pattern.reduce((sum, r) => sum + (r.loss * r.quantity), 0);
                    
                    const rodSaving = basicRods - patternRods;
                    const wasteSaving = basicWaste - patternWaste;
                    const percentSaving = ((rodSaving / basicRods) * 100).toFixed(1);
                    
                    if (rodSaving > 0) {
                      return `Column Generation Algorithm으로 막대 ${rodSaving}개(${percentSaving}%) 절약, 폐기물 ${wasteSaving}mm 감소!`;
                    } else if (rodSaving < 0) {
                      return `이 경우에는 Branch and Bound Algorithm이 ${Math.abs(rodSaving)}개 더 효율적입니다.`;
                    } else {
                      return `두 알고리즘의 막대 사용량이 동일합니다.`;
                    }
                  })()}
                  type={(() => {
                    const basicRods = comparisonResults.basic.reduce((sum, r) => sum + r.quantity, 0);
                    const patternRods = comparisonResults.pattern.reduce((sum, r) => sum + r.quantity, 0);
                    return patternRods < basicRods ? 'success' : patternRods > basicRods ? 'warning' : 'info';
                  })()}
                  showIcon
                />
              </Col>
            </Row>

            {/* 상세 비교 탭 */}
            <Tabs defaultActiveKey="1">
              <Tabs.TabPane tab="🔵 Branch and Bound Algorithm" key="1">
                <Table
                  dataSource={comparisonResults.basic.map((item, index) => ({
                    ...item,
                    key: index,
                    efficiency: ((1 - item.loss / rodLength) * 100).toFixed(1)
                  }))}
                  columns={[
                    { title: 'Case', dataIndex: 'case', key: 'case', render: (text) => <Tag color="blue">{text}</Tag> },
                    { title: '수량', dataIndex: 'quantity', key: 'quantity', align: 'center', render: (text) => `${text}개` },
                    { title: '총 길이', dataIndex: 'totalLength', key: 'totalLength', align: 'center', render: (text) => `${text}mm` },
                    { title: 'Loss', dataIndex: 'loss', key: 'loss', align: 'center', render: (text) => <Tag color={text === 0 ? 'green' : 'red'}>{text}mm</Tag> },
                    { title: '효율성', dataIndex: 'efficiency', key: 'efficiency', align: 'center', render: (text) => `${text}%` }
                  ]}
                  pagination={false}
                  size="small"
                />
              </Tabs.TabPane>
              <Tabs.TabPane tab="🚀 Column Generation Algorithm" key="2">
                <Table
                  dataSource={comparisonResults.pattern.map((item, index) => ({
                    ...item,
                    key: index,
                    efficiency: ((1 - item.loss / rodLength) * 100).toFixed(1)
                  }))}
                  columns={[
                    { title: 'Case', dataIndex: 'case', key: 'case', render: (text) => <Tag color="green">{text}</Tag> },
                    { title: '수량', dataIndex: 'quantity', key: 'quantity', align: 'center', render: (text) => `${text}개` },
                    { title: '총 길이', dataIndex: 'totalLength', key: 'totalLength', align: 'center', render: (text) => `${text}mm` },
                    { title: 'Loss', dataIndex: 'loss', key: 'loss', align: 'center', render: (text) => <Tag color={text === 0 ? 'green' : 'red'}>{text}mm</Tag> },
                    { title: '효율성', dataIndex: 'efficiency', key: 'efficiency', align: 'center', render: (text) => `${text}%` }
                  ]}
                  pagination={false}
                  size="small"
                />
              </Tabs.TabPane>
            </Tabs>
          </Card>
        )}
      </Content>
    </Layout>
  );
};

// Rod Cutting 알고리즘 클래스 (기존 로직 재사용)
class RodCuttingOptimizer {
  constructor(rodLength, requirements) {
    this.originalRodLength = rodLength;
    this.rodLength = rodLength;
    this.requirements = requirements.map(req => ({
      length: req.length,
      quantity: req.quantity
    }));
    this.lengths = this.requirements.map(req => req.length);
    this.requiredPieces = this.requirements.map(req => req.quantity);
  }

  optimize() {
    let results = [];
    let remainingPieces = [...this.requiredPieces];
    let whileCount = 0;
    this.rodLength = this.originalRodLength;

    while (this.hasRemainingRequirements(remainingPieces) && whileCount < 100) {
      whileCount++;
      
      const combinations = this.findCombinations(this.rodLength);
      
      if (combinations.length === 0) {
        this.rodLength -= 10;
        continue;
      }

      this.deleteInvalidItems(combinations, remainingPieces);

      // 🔧 Java와 동일한 for 루프 로직 (i = items.size(); i > 0; i--)
      for (let i = combinations.length; i > 0; i--) {
        const combination = combinations[i - 1]; // Java: items.get(i-1)
        const repeatCount = this.calculateRepeatCount(combination, remainingPieces);
        
        if (repeatCount === 0) continue;

        const totalLength = this.calculateTotalLength(combination);
        const loss = this.originalRodLength - totalLength;
        
        // 디버깅 로그 (임시)
        console.log(`DEBUG: originalRodLength=${this.originalRodLength}, totalLength=${totalLength}, loss=${loss}`);
        
        results.push({
          case: `Case-${results.length + 1}`,
          quantity: repeatCount,
          cuts: combination,
          lengthHeaders: this.lengths.map((len, idx) => `길이-${idx + 1}(${len}mm)`),
          totalLength: totalLength,
          loss: loss
        });

        // Java: createRods() - 요구사항에서 차감
        for (let j = 0; j < remainingPieces.length; j++) {
          remainingPieces[j] -= combination[j] * repeatCount;
        }

        // Java: i = i - removeCnt + 1
        const removedCount = this.deleteInvalidItems(combinations, remainingPieces);
        i = i - removedCount + 1;
      }

      this.rodLength -= 10;
    }

    return results;
  }

  findCombinations(rodLength) {
    const combinations = [];
    const lengths = this.lengths;
    
    const generateCombinations = (currentCombination, lengthIndex) => {
      if (lengthIndex >= lengths.length) {
        const totalLength = this.calculateTotalLength(currentCombination);
        if (totalLength === rodLength) {
          combinations.push([...currentCombination]);
        }
        return;
      }

      const maxCount = Math.floor(rodLength / lengths[lengthIndex]);
      for (let count = 0; count <= maxCount; count++) {
        currentCombination[lengthIndex] = count;
        generateCombinations(currentCombination, lengthIndex + 1);
      }
    };

    generateCombinations(new Array(lengths.length).fill(0), 0);
    return combinations;
  }

  calculateTotalLength(combination) {
    let total = 0;
    for (let i = 0; i < combination.length; i++) {
      total += combination[i] * this.lengths[i];
    }
    return total;
  }

  calculateRepeatCount(combination, remainingPieces) {
    let minRepeat = Infinity;
    
    for (let i = 0; i < combination.length; i++) {
      if (combination[i] > 0 && remainingPieces[i] > 0) {
        const possibleRepeats = Math.floor(remainingPieces[i] / combination[i]);
        minRepeat = Math.min(minRepeat, possibleRepeats);
      }
    }
    
    return minRepeat === Infinity ? 0 : minRepeat;
  }

  deleteInvalidItems(combinations, remainingPieces) {
    // 🔧 Java 로직과 정확히 일치하도록 수정
    let removedCount = 0;
    
    for (let lengthIdx = 0; lengthIdx < remainingPieces.length; lengthIdx++) {
      for (let i = combinations.length - 1; i >= 0; i--) {
        // Java: if (rp[i] < items.get(j).get(i))
        if (remainingPieces[lengthIdx] < combinations[i][lengthIdx]) {
          combinations.splice(i, 1);
          removedCount++;
        }
      }
    }
    
    return removedCount;
  }

  hasRemainingRequirements(remainingPieces) {
    return remainingPieces.some(count => count > 0);
  }
}

// 🚀 새로운 패턴 기반 Rod Cutting 알고리즘 (0폐기 우선)
class PatternBasedOptimizer {
  constructor(rodLength, requirements) {
    this.stockLength = rodLength;
    this.originalRodLength = rodLength;
    this.requirements = requirements.map(req => ({
      length: req.length,
      quantity: req.quantity
    }));
    this.sizes = this.requirements.map(req => req.length);
    this.demand = this.requirements.map(req => req.quantity);
  }

  optimize() {
    const patterns = this.generateAllPatterns();
    const result = this.plan(patterns);
    return this.formatResults(result);
  }

  // 🎯 핵심: 모든 가능한 패턴 생성 (DFS + 가지치기)
  generateAllPatterns() {
    const n = this.sizes.length;
    
    // 큰 길이 우선으로 정렬 (가지치기 효과 증가)
    const order = Array.from({length: n}, (_, i) => i);
    order.sort((a, b) => this.sizes[b] - this.sizes[a]);
    
    const sortedSizes = order.map(i => this.sizes[i]);
    const sortedDemand = order.map(i => this.demand[i]);
    
    const maxCount = sortedSizes.map((size, i) => 
      Math.min(sortedDemand[i], Math.floor(this.stockLength / size))
    );
    
    const results = [];
    const curCounts = new Array(n).fill(0);
    
    this.dfsGenerate(0, sortedSizes, maxCount, curCounts, 0, results);
    
    // 원래 인덱스 순서로 복원
    const restored = results.map(pattern => {
      const restoredCounts = new Array(n).fill(0);
      for (let i = 0; i < n; i++) {
        restoredCounts[order[i]] = pattern.counts[i];
      }
      return new Pattern(restoredCounts, pattern.used, this.stockLength);
    });
    
    // 빈 패턴 제거 및 중복 제거
    const filtered = restored.filter(p => p.used > 0);
    const unique = this.removeDuplicates(filtered);
    
    // 폐기 적은 순, 사용량 큰 순으로 정렬
    unique.sort((a, b) => {
      if (a.waste !== b.waste) return a.waste - b.waste;
      return b.used - a.used;
    });
    
    return unique;
  }

  dfsGenerate(idx, sizes, maxCount, curCounts, curUsed, results) {
    if (curUsed > this.stockLength) return; // 가지치기
    
    if (idx === sizes.length) {
      results.push(new Pattern([...curCounts], curUsed, this.stockLength));
      return;
    }
    
    const size = sizes[idx];
    for (let c = 0; c <= maxCount[idx]; c++) {
      const nextUsed = curUsed + c * size;
      if (nextUsed > this.stockLength) break; // 조기 종료
      
      curCounts[idx] = c;
      this.dfsGenerate(idx + 1, sizes, maxCount, curCounts, nextUsed, results);
    }
    curCounts[idx] = 0;
  }

  removeDuplicates(patterns) {
    const unique = new Map();
    for (const pattern of patterns) {
      const key = pattern.counts.join(',');
      if (!unique.has(key)) {
        unique.set(key, pattern);
      }
    }
    return Array.from(unique.values());
  }

  // 🎯 핵심: 계획 수립 (0폐기 패턴 우선 적용)
  plan(patterns) {
    const result = new PlanResult();
    result.stockLength = this.stockLength;
    
    const remain = [...this.demand];
    
    // 1단계: 0폐기 패턴들을 우선 적용
    const zeroWastePatterns = patterns.filter(p => p.waste === 0);
    zeroWastePatterns.sort((a, b) => b.used - a.used); // 사용량 큰 순
    
    let usedZeroWaste = true;
    while (usedZeroWaste) {
      usedZeroWaste = false;
      
      for (const pattern of zeroWastePatterns) {
        const times = this.maxApplicableTimes(pattern, remain);
        if (times > 0) {
          this.applyPattern(pattern, remain, times);
          result.add(pattern, times);
          usedZeroWaste = true;
          break; // 하나씩 적용하고 다시 최적을 찾음
        }
      }
    }
    
    // 2단계: 남은 수요에 대해 폐기 최소 패턴 적용
    while (!this.allSatisfied(remain)) {
      let bestPattern = null;
      let bestWaste = Infinity;
      let bestUsed = -1;
      
      for (const pattern of patterns) {
        if (!this.fitsRemain(pattern, remain)) continue;
        
        if (pattern.waste < bestWaste || 
           (pattern.waste === bestWaste && pattern.used > bestUsed)) {
          bestWaste = pattern.waste;
          bestUsed = pattern.used;
          bestPattern = pattern;
        }
      }
      
      if (!bestPattern) {
        // 강제 패턴 생성 (안전장치)
        const pickIdx = remain.findIndex(r => r > 0);
        if (pickIdx < 0) break;
        
        const forcedCounts = new Array(this.sizes.length).fill(0);
        forcedCounts[pickIdx] = Math.min(remain[pickIdx], 
          Math.floor(this.stockLength / this.sizes[pickIdx]));
        const used = forcedCounts[pickIdx] * this.sizes[pickIdx];
        
        if (used === 0) break;
        bestPattern = new Pattern(forcedCounts, used, this.stockLength);
      }
      
      const times = this.maxApplicableTimes(bestPattern, remain);
      const actualTimes = Math.max(times, 1);
      
      this.applyPattern(bestPattern, remain, actualTimes);
      result.add(bestPattern, actualTimes);
    }
    
    return result;
  }

  maxApplicableTimes(pattern, remain) {
    let times = Infinity;
    let hasAny = false;
    
    for (let i = 0; i < remain.length; i++) {
      if (pattern.counts[i] > 0) {
        hasAny = true;
        times = Math.min(times, Math.floor(remain[i] / pattern.counts[i]));
      }
    }
    
    return hasAny ? times : 0;
  }

  applyPattern(pattern, remain, times) {
    for (let i = 0; i < remain.length; i++) {
      remain[i] -= pattern.counts[i] * times;
    }
  }

  fitsRemain(pattern, remain) {
    for (let i = 0; i < remain.length; i++) {
      if (pattern.counts[i] > remain[i]) return false;
    }
    return true;
  }

  allSatisfied(remain) {
    return remain.every(r => r <= 0);
  }

  formatResults(planResult) {
    const results = [];
    let caseIndex = 1;
    
    for (const [key, [pattern, times]] of planResult.patternUsage.entries()) {
      results.push({
        case: `Pattern-${caseIndex++}`,
        quantity: times,
        cuts: pattern.counts,
        lengthHeaders: this.sizes.map((len, idx) => `길이-${idx + 1}(${len}mm)`),
        totalLength: pattern.used,
        loss: pattern.waste,
        algorithm: 'Pattern',
        isZeroWaste: pattern.waste === 0
      });
    }
    
    return results;
  }
}

// Pattern 클래스 (패턴 정보 저장)
class Pattern {
  constructor(counts, used, stockLength) {
    this.counts = [...counts];
    this.used = used;
    this.stockLength = stockLength;
    this.waste = stockLength - used;
  }
}

// PlanResult 클래스 (결과 정보 저장)
class PlanResult {
  constructor() {
    this.patternUsage = new Map();
    this.barsUsed = 0;
    this.totalWaste = 0;
    this.totalUsed = 0;
    this.stockLength = 0;
  }

  add(pattern, times) {
    if (times <= 0) return;
    
    const key = pattern.counts.join(',');
    if (this.patternUsage.has(key)) {
      const existing = this.patternUsage.get(key);
      this.patternUsage.set(key, [existing[0], existing[1] + times]);
    } else {
      this.patternUsage.set(key, [pattern, times]);
    }
    
    this.barsUsed += times;
    this.totalWaste += pattern.waste * times;
    this.totalUsed += pattern.used * times;
  }
}

export default RodCuttingAlgo;
